import * as z from "zod";
import { mutator } from "./mutator";

// A UI-issued temporary credential's lifetime, matching the device flow's band:
// long enough for a workday by default, a long day at most. The credential
// expires on its own; there is no refresh.
export const SESSION_CRED_DEFAULT_TTL_SECONDS = 8 * 60 * 60;
export const SESSION_CRED_MAX_TTL_SECONDS = 16 * 60 * 60;

function clampTtl(ttlSeconds: number | undefined): number {
  if (ttlSeconds == null || !Number.isFinite(ttlSeconds) || ttlSeconds <= 0) {
    return SESSION_CRED_DEFAULT_TTL_SECONDS;
  }
  return Math.min(ttlSeconds, SESSION_CRED_MAX_TTL_SECONDS);
}

export const createServiceAccountSessionSchema = z.object({
  id: z.string(),
  start: z.string(),
  key: z.string(),
  serviceAccountId: z.string(),
  ttlSeconds: z.number().optional(),
});

export const createServiceAccountSession = mutator({
  redactKeys: ["key"],
  schema: createServiceAccountSessionSchema,
  resolve: {
    serviceAccount: {
      kind: "serviceAccount",
      from: "serviceAccountId",
      optional: true,
    },
  },
  permissions: [
    {
      permission: "service_account:assume",
      scopeId: ({ args }) => args.serviceAccountId,
    },
  ],
  fn: async ({
    tx,
    ctx,
    args: { id, start, serviceAccountId, ttlSeconds },
  }) => {
    const now = Date.now();
    await tx.mutate.apikey.insert({
      id,
      // The `session:` prefix marks this as a short-lived, UI-issued credential
      // so it's filtered out of the service account's human-managed key list.
      name: `session:${id}`,
      start,
      referenceId: ctx.userId,
      spaceId: null,
      serviceAccountId,
      enabled: true,
      expiresAt: now + clampTtl(ttlSeconds) * 1000,
      createdAt: now,
      updatedAt: now,
      tenantId: ctx.tenantId,
    });
  },
});
